import { Suspense, lazy, useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import {
  type AetheriaConfig,
  AETHERIA_DEFAULTS,
  makeTokenContext,
  resolveTokens,
} from "@wedding/shared";

gsap.registerPlugin(ScrollTrigger);

// Three.js only downloads when this layout renders.
const AetheriaScene = lazy(() => import("./scene"));

interface AetheriaLayoutProps extends JoinLayoutProps {
  config?: AetheriaConfig;
}

export function AetheriaLayout({
  formSlot,
  program,
  title,
  dateTime,
  location,
  description,
  galleryImages,
  config,
}: AetheriaLayoutProps) {
  const cfg = config ?? AETHERIA_DEFAULTS;
  const lenisRef = useRef<Lenis | null>(null);

  const ctx = makeTokenContext({ title, dateTime, location });
  const t = (s: string) => resolveTokens(s, ctx);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.4, easing: (x) => 1 - Math.pow(1 - x, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const gctx = gsap.context(() => {
      // Hero text side — stagger in from right
      gsap.from(".aet-hero-label", { opacity: 0, x: 30, duration: 1.2, ease: "expo.out", delay: 0.3 });
      gsap.from(".aet-hero-title", { opacity: 0, x: 50, duration: 1.5, ease: "expo.out", delay: 0.5 });
      gsap.from(".aet-hero-tag", { opacity: 0, x: 30, duration: 1.2, ease: "expo.out", delay: 0.9 });
      gsap.from(".aet-hero-meta", { opacity: 0, x: 20, duration: 1, ease: "expo.out", delay: 1.1 });

      // Section reveals
      gsap.from(".aet-story-line", {
        opacity: 0, y: 40, stagger: 0.1, duration: 1, ease: "power3.out",
        scrollTrigger: { trigger: ".aet-story", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
      gsap.from(".aet-date-number", {
        opacity: 0, scale: 0.85, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".aet-date", start: "top 70%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      gctx.revert();
    };
  }, [cfg]);

  const d = dateTime ? new Date(dateTime) : null;
  const storyLines = (description ?? "").split(/(?<=[.!?])\s+/).filter(Boolean);

  return (
    <main
      className="relative overflow-x-hidden"
      style={{
        background: cfg.colors.background,
        color: cfg.colors.text,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* ═══ HERO — mobile: ring top + text bottom / desktop: side-by-side ══ */}
      <section className="relative min-h-screen flex flex-col md:grid md:grid-cols-2">

        {/* ── Ring (top on mobile, left on desktop) ── */}
        <div className="relative flex items-center justify-center
                        h-[70vw] max-h-[420px]
                        md:h-auto md:max-h-none md:min-h-screen">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse 60% 60% at 50% 50%, ${cfg.colors.accent}18, transparent 70%)` }}
          />
          {/* aspect-square wrapper → absolute inset-0 inner → canvas fills */}
          <div className="relative w-[72%] sm:w-[60%] md:w-[72%] aspect-square pointer-events-none">
            <div className="absolute inset-0">
              <Suspense fallback={null}>
                <AetheriaScene config={cfg} />
              </Suspense>
            </div>
          </div>
        </div>

        {/* ── Text (bottom on mobile, right on desktop) ── */}
        <div className="flex flex-col justify-center
                        px-6 sm:px-10 md:px-14
                        pb-16 pt-4 md:py-0
                        text-center md:text-left">
          <p
            className="aet-hero-label text-[10px] uppercase tracking-[0.5em] mb-5 opacity-60"
            style={{ color: cfg.colors.accent }}
          >
            {t(cfg.hero.label)}
          </p>

          <h1
            className="aet-hero-title font-light leading-[0.92] tracking-[-0.03em] mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(2.6rem, 8vw, 7rem)",
            }}
          >
            {ctx.titleFirst && ctx.titleSecond ? (
              <>
                {ctx.titleFirst}
                <br />
                <span
                  className="opacity-40 not-italic"
                  style={{ fontSize: "0.55em", letterSpacing: "0.12em", fontFamily: "inherit" }}
                >
                  &amp;
                </span>
                <br />
                {ctx.titleSecond}
              </>
            ) : title}
          </h1>

          <p
            className="aet-hero-tag opacity-70 mb-6 leading-relaxed text-base sm:text-lg"
          >
            {t(cfg.hero.tagline)}
          </p>

          <div className="aet-hero-meta space-y-1 text-sm">
            {ctx.dayMonth !== "—" && (
              <p className="tracking-[0.2em] uppercase" style={{ color: cfg.colors.accent }}>
                {ctx.dayMonth}.{ctx.year}
              </p>
            )}
            {location && (
              <p className="opacity-50">{ctx.locationCity}</p>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3 mt-10 opacity-40">
            <div className="w-8 h-px" style={{ background: cfg.colors.accent }} />
            <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
          </div>
        </div>
      </section>

      {/* ═══ STORY ═════════════════════════════════════════════════════ */}
      <section
        className="aet-story relative px-6 sm:px-16 py-32"
        style={{ background: `linear-gradient(to bottom, ${cfg.colors.background}, ${cfg.colors.background}f0)` }}
      >
        <div className="max-w-3xl mx-auto">
          <p className="aet-story-line text-[10px] uppercase tracking-[0.5em] mb-10" style={{ color: cfg.colors.accent }}>
            {t(cfg.story.label)}
          </p>
          <h2
            className="aet-story-line font-light leading-[1.05] tracking-tight mb-14 whitespace-pre-line"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          >
            {t(cfg.story.headline)}
          </h2>
          {storyLines.map((line, i) => (
            <p key={i} className="aet-story-line text-lg leading-relaxed opacity-80 mb-5 max-w-2xl">
              {line.trim()}
            </p>
          ))}
        </div>
      </section>

      {/* ═══ DATE ══════════════════════════════════════════════════════ */}
      <section className="aet-date relative px-6 sm:px-16 py-32 text-center" style={{ background: cfg.colors.background }}>
        <p className="text-[10px] uppercase tracking-[0.6em] mb-8" style={{ color: cfg.colors.accent }}>
          {t(cfg.date.label)}
        </p>
        <div
          className="aet-date-number font-light leading-none tabular-nums tracking-[-0.05em]"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 18vw, 16rem)" }}
        >
          {ctx.dayMonth}
        </div>
        <p
          className="font-light tracking-widest opacity-60 mt-3"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 4vw, 3rem)" }}
        >
          {ctx.year}
        </p>
        <p className="text-sm tracking-[0.3em] uppercase opacity-50 mt-10">
          {d?.toLocaleDateString("tr-TR", { weekday: "long" })} · {ctx.time}
        </p>
      </section>

      {/* ═══ VENUE ═════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen overflow-hidden">
        {galleryImages[0] && (
          <img
            src={galleryImages[0]}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(0.4) brightness(0.7)" }}
          />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${cfg.colors.background}, transparent 50%)` }} />
        <div className="relative z-10 px-6 sm:px-16 py-32 flex flex-col justify-end min-h-screen max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: cfg.colors.accent }}>
            {t(cfg.venue.label)}
          </p>
          <h2 className="font-light leading-tight tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
            {location ?? "—"}
          </h2>
        </div>
      </section>

      {/* ═══ RSVP ══════════════════════════════════════════════════════ */}
      <section className="min-h-screen flex items-center justify-center px-6 py-32" style={{ background: cfg.colors.background }}>
        <div className="w-full max-w-md text-center">
          <p className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: cfg.colors.accent }}>
            {t(cfg.rsvp.label)}
          </p>
          <h2 className="font-light leading-tight mb-10" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            {t(cfg.rsvp.heading)}
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
          <p className="mt-10 text-[10px] tracking-[0.4em] uppercase opacity-50">
            {t(cfg.rsvp.closingPrefix)}
          </p>
        </div>
      </section>
    </main>
  );
}
